import * as React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RichTextEditor } from './components/RichTextEditor';
import CancelIcon from '@mui/icons-material/Cancel';
import { sanitizeTelegramHtml } from './utils';

const API_URL = import.meta.env.VITE_API_URL;

type OrderField =
  | 'history_card_header'
  | 'history_card_number'
  | 'history_card_goods'
  | 'history_card_total_sum'
  | 'history_card_footer';

type OrderType = 'history_card';

type OrderCardItem = {
  orderCardId: number;
  value: string;
  field: OrderField;
  isActive: boolean | null;
  index: number;
  type: OrderType;
  spaces: number | null;
};

const FIELD_LABELS: Record<OrderField, string> = {
  history_card_header: 'Заголовок сторінки історії',
  history_card_number: 'Номер замовлення',
  history_card_goods:
    'Шаблон товарів. Використовуйте макрос ${name} для відображення назви товару, ${quantity} для кількості та ${price} для ціни',
  history_card_total_sum: 'Сума всього замовлення',
  history_card_footer: 'Повідомлення під історією користувача',
};

function SpacesToggle({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        Відступ
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={(_, v) => v !== null && onChange(v)}
      >
        {[0, 1, 2, 3, 4, 5].map((n: number) => (
          <ToggleButton key={n} value={n}>
            {n}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}

function SortableRow({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: item.orderCardId,
    });

  return (
    <Paper
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ p: 2 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>

          <Typography variant="caption" flex={1}>
            {FIELD_LABELS[item.field]}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={!!item.isActive}
                onChange={(e) =>
                  onChange(item.orderCardId, { isActive: e.target.checked })
                }
              />
            }
            label="Відобразити"
          />
        </Stack>

        <RichTextEditor
          value={item.value}
          onChange={(v: string) => onChange(item.orderCardId, { value: v })}
        />

        <SpacesToggle
          value={item.spaces ?? 0}
          onChange={(v) => onChange(item.orderCardId, { spaces: v })}
        />
      </Stack>
    </Paper>
  );
}

function FooterBlock({
  item,
  onChange,
}: {
  item: OrderCardItem;
  onChange: (id: number, patch: Partial<OrderCardItem>) => void;
}) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="caption">{FIELD_LABELS[item.field]}</Typography>

      <RichTextEditor value={item.value} onChange={() => {}} minRows={3} />
    </Paper>
  );
}

export default function ModuleHistory() {
  const [isMacrosOn, setIsMacrosOn] = React.useState<boolean | null>(null);
  const [items, setItems] = React.useState<OrderCardItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetch(`${API_URL}/settings`)
      .then((r) => r.json())
      .then((res) => setIsMacrosOn(res.settings.isMacrosOn))
      .catch(() => setIsMacrosOn(false));

    fetch(`${API_URL}/order-card?type=history_card`)
      .then((r) => r.json())
      .then((res: { data: OrderCardItem[] }) => {
        setItems(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Не вдалося завантажити дані');
        setLoading(false);
      });
  }, []);

  const updateItem = async (id: number, patch: Partial<OrderCardItem>) => {
    const newPatch: Partial<OrderCardItem> = { ...patch };

    if (patch.value !== undefined) {
      newPatch.value = sanitizeTelegramHtml(patch.value);
    }

    setItems((prev) =>
      prev.map((i) => (i.orderCardId === id ? { ...i, ...newPatch } : i)),
    );

    await fetch(`${API_URL}/order-card/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPatch),
    });
  };

  const onDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((i) => i.orderCardId === active.id);
    const newIndex = items.findIndex((i) => i.orderCardId === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    await fetch(`${API_URL}/order-card-reorder`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order: newItems
          .filter((i) => i.field !== 'history_card_footer')
          .map((i, index: number) => ({
            id: i.orderCardId,
            index,
          })),
      }),
    });
  };

  const sortableItems: OrderCardItem[] = items.filter(
    (i) => i.field !== 'history_card_footer',
  );

  const footer = items.find((i) => i.field === 'history_card_footer');

  if (loading)
    return (
      <Box p={3}>
        <Typography>Завантаження...</Typography>
      </Box>
    );

  if (error)
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );

  if (isMacrosOn) {
    return (
      <Alert severity={'error'} icon={<CancelIcon />} sx={{ mb: 3, mt: 2 }}>
        <b>Увага:</b> Через використання макросів розділ цих налаштувань
        недоступний. Шоб використовувати ці налаштування вимкніть макроси у{' '}
        <a href="/settings">налаштуваннях</a>.
      </Alert>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" mb={3}>
        Історія замовлень
      </Typography>

      <Stack spacing={4}>
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={sortableItems.map((i) => i.orderCardId)}
            strategy={verticalListSortingStrategy}
          >
            <Stack spacing={2}>
              {sortableItems.map((item) => (
                <SortableRow
                  key={item.orderCardId}
                  item={item}
                  onChange={updateItem}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>

        {footer && (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" mb={2}>
              Текст в кінці повідомлення
            </Typography>
            <FooterBlock item={footer} onChange={updateItem} />
          </Box>
        )}
      </Stack>
    </Box>
  );
}
